import { Badge, Card, Group, Stack, Text, Title } from '@mantine/core';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { useEffect, useRef } from 'react';
import type { ObjectView } from '../lib/api.js';

/** Runder Marker mit Emoji statt der (im Bundler kaputten) Leaflet-Default-Icons. */
function pin(color: string, label: string): L.DivIcon {
  return L.divIcon({
    className: '',
    html: `<div style="background:${color};width:26px;height:26px;border-radius:50%;display:flex;align-items:center;justify-content:center;box-shadow:0 1px 4px rgba(0,0,0,0.45);border:2px solid #fff;font-size:14px;line-height:1;">${label}</div>`,
    iconSize: [26, 26],
    iconAnchor: [13, 13],
    popupAnchor: [0, -14],
  });
}

const OBJECT_COLOR = '#7c3aed';
const PRIMARY_COLOR = '#2563eb';
const SECONDARY_COLOR = '#0ea5e9';

/**
 * Karte mit Objekt-Pin + profil-abhängigen Ankern (A: Arbeit + Haringsee + Elke,
 * B: Hackengasse). Gestrichelte Linien Objekt→Anker, Popups mit bester Pendelzeit.
 * Leaflet imperativ (kein react-leaflet) — eine Dependency, volle Kontrolle.
 */
export function MapCard({ object }: { object: ObjectView }): JSX.Element {
  const containerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<L.Map | null>(null);

  const hasCoords = object.lat !== null && object.lng !== null;
  const isBuero = object.assignedProfile === 'B_buero';
  // Das Inserat selbst: bei Büro ein Büro-Angebot (🏢), bei Wohnung eine Wohnung (🏠).
  const objEmoji = isBuero ? '🏢' : '🏠';
  const objLabel = isBuero ? 'Büro (Angebot)' : 'Wohnung (Angebot)';

  // Dynamische Legende: Objekt + eindeutige Anker (Arbeit / Eltern / Zuhause).
  const legend: Array<{ emoji: string; label: string }> = [
    { emoji: objEmoji, label: isBuero ? 'Büro' : 'Wohnung' },
  ];
  const seenLegend = new Set<string>();
  for (const c of object.commutes) {
    const short = c.displayName.replace(/\s*\(.*$/, '');
    const key = `${c.icon ?? ''}${short}`;
    if (seenLegend.has(key)) continue;
    seenLegend.add(key);
    legend.push({ emoji: c.icon ?? '📍', label: short });
  }

  useEffect(() => {
    if (!containerRef.current || !hasCoords) return;
    const objLatLng: [number, number] = [object.lat as number, object.lng as number];

    const map = L.map(containerRef.current, {
      scrollWheelZoom: false,
      zoomControl: true,
    });
    mapRef.current = map;

    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      maxZoom: 19,
      attribution: '&copy; OpenStreetMap',
    }).addTo(map);

    const points: Array<[number, number]> = [objLatLng];

    L.marker(objLatLng, { icon: pin(OBJECT_COLOR, objEmoji) })
      .addTo(map)
      .bindPopup(
        `<b>${objLabel}</b><br>${object.listing?.title ?? ''}<br>${object.address ?? ''}`,
      );

    for (const c of object.commutes) {
      const ll: [number, number] = [c.lat, c.lng];
      points.push(ll);
      const color = c.role === 'primary' ? PRIMARY_COLOR : SECONDARY_COLOR;
      const mins = c.bestMinutes !== null ? `${Math.round(c.bestMinutes)} min` : '—';
      L.marker(ll, { icon: pin(color, c.icon ?? '📍') })
        .addTo(map)
        .bindPopup(`<b>${c.displayName}</b><br>🚆/🚗 best: ${mins}`);
      L.polyline([objLatLng, ll], {
        color,
        weight: 2,
        opacity: 0.5,
        dashArray: '4 6',
      }).addTo(map);
    }

    map.fitBounds(L.latLngBounds(points), { padding: [40, 40], maxZoom: 13 });
    // Container wird ggf. erst nach Mount final dimensioniert.
    const t = setTimeout(() => map.invalidateSize(), 120);

    return () => {
      clearTimeout(t);
      map.remove();
      mapRef.current = null;
    };
  }, [object, hasCoords]);

  return (
    <Card padding="lg">
      <Stack gap="sm">
        <Group justify="space-between" wrap="wrap" gap="xs">
          <Title order={4}>Karte</Title>
          <Group gap={6} wrap="wrap">
            {legend.map((l) => (
              <Badge key={l.label} size="xs" variant="light" color="gray" tt="none">
                {l.emoji} {l.label}
              </Badge>
            ))}
          </Group>
        </Group>

        {hasCoords ? (
          <div
            ref={containerRef}
            style={{
              height: 320,
              width: '100%',
              borderRadius: 8,
              overflow: 'hidden',
              zIndex: 0,
            }}
          />
        ) : (
          <Text c="dimmed" size="sm">
            Keine Koordinaten — Objekt noch nicht (genau) geocodet.
          </Text>
        )}

        {object.geocodePrecision && object.geocodePrecision !== 'rooftop' && (
          <Text size="xs" c="dimmed">
            ⚠ Geocode-Genauigkeit: {object.geocodePrecision} — Pin evtl. ungenau.
          </Text>
        )}
      </Stack>
    </Card>
  );
}
